import { useState } from "react";

import dayjs from "dayjs";
import { Download, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import * as XLSX from "xlsx";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { ALIACE_SPA_ID } from "~/constants/companies";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { useImportSupplierBalances } from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { formatCurrency } from "~/utils/helpers";

import type { SupplierBalanceImportRow } from "~/services/purchasing";

interface SupplierBalanceUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ParseResult {
  rows: SupplierBalanceImportRow[];
  total: number;
  skipped: number; // total/subtotal rows without código
  names: Record<string, string>; // código -> nombre, for the "sin proveedor" warning
  fatal?: string;
}

interface UnmatchedDetail {
  code: string;
  nombre: string;
  monto: number;
}

interface ImportWarning {
  imported: number;
  details: UnmatchedDetail[];
  total: number; // sum of saldo not imported
}

const str = (v: unknown) => (v == null ? "" : String(v).trim());

const toNum = (v: unknown): number => {
  if (typeof v === "number") return v;
  const s = str(v).replace(/[^0-9,.-]/g, "");
  if (!s) return 0;
  // Chilean/US thousands: drop dots used as thousands, comma as decimal
  const normalized =
    s.includes(",") && s.includes(".")
      ? s.replace(/\./g, "").replace(",", ".")
      : s.replace(",", ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const toIso = (v: unknown): string | null => {
  if (v == null || v === "") return null;
  if (v instanceof Date) return dayjs(v).format("YYYY-MM-DD");
  const d = dayjs(str(v));
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
};

// "2103001" -> "2-1-03-001"
const apCode = (v: unknown, isForeign: boolean): string => {
  const d = str(v).replace(/[^0-9]/g, "");
  if (d.length === 7) return `${d[0]}-${d[1]}-${d.slice(2, 4)}-${d.slice(4)}`;
  return isForeign ? "2-1-03-002" : "2-1-03-001";
};

/**
 * Parse the Softland "lista de deudas" (partidas abiertas) export — one sheet per cuenta
 * (Exterior 2103002 / Nacional 2103001), columns:
 *   Codigo · Nombre · Cuenta · Documento Tipo · Nº · Fecha Emision · Fecha Vcto. · Tipo de cambio · Saldo
 * Trailing total rows (blank código) are skipped. is_foreign is inferred from the cuenta.
 */
function parseWorkbook(data: ArrayBuffer): ParseResult {
  const wb = XLSX.read(data, { type: "binary", cellDates: true });
  const rows: SupplierBalanceImportRow[] = [];
  const names: Record<string, string> = {};
  let skipped = 0;
  let sawHeader = false;

  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName];
    const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, {
      header: 1,
      raw: true,
      blankrows: false,
    });
    if (grid.length === 0) continue;
    const header = (grid[0] as unknown[]).map((h) => str(h).toLowerCase());
    const col = (name: string) => header.indexOf(name.toLowerCase());
    const idx = {
      codigo: col("codigo"),
      nombre: col("nombre"),
      cuenta: col("cuenta"),
      tipo: col("documento tipo"),
      folio: col("nº"),
      emision: col("fecha emision"),
      vcto: col("fecha vcto."),
      tc: header.findIndex((h) => h.startsWith("tipo de cambio")),
      saldo: col("saldo"),
    };
    if (idx.codigo < 0 || idx.saldo < 0) continue; // not a balances sheet
    sawHeader = true;
    const sheetForeign = /exterior|extranjer/i.test(sheetName);

    for (let i = 1; i < grid.length; i++) {
      const r = grid[i] as unknown[] | undefined;
      if (!r) continue;
      const codigo = str(r[idx.codigo]);
      if (!codigo || /^total/i.test(codigo)) {
        if (str(r[idx.saldo])) skipped++; // trailing grand-total row
        continue;
      }
      const cuenta = idx.cuenta >= 0 ? str(r[idx.cuenta]) : "";
      const isForeign = cuenta ? cuenta.replace(/[^0-9]/g, "").endsWith("002") : sheetForeign;
      if (idx.nombre >= 0 && !names[codigo]) {
        const nombre = str(r[idx.nombre]);
        if (nombre) names[codigo] = nombre;
      }
      rows.push({
        codigo,
        ap_account_code: apCode(cuenta, isForeign),
        doc_tipo_code: idx.tipo >= 0 ? str(r[idx.tipo]) || null : null,
        folio: idx.folio >= 0 ? str(r[idx.folio]) || null : null,
        emission_date: idx.emision >= 0 ? toIso(r[idx.emision]) : null,
        due_date: idx.vcto >= 0 ? toIso(r[idx.vcto]) : null,
        saldo_clp: toNum(r[idx.saldo]),
        exchange_rate: idx.tc >= 0 ? toNum(r[idx.tc]) || null : null,
        is_foreign: isForeign,
      });
    }
  }

  if (!sawHeader) {
    return {
      rows: [],
      total: 0,
      skipped: 0,
      names: {},
      fatal:
        "No se reconocieron las columnas. Use el formato base (Codigo · Nombre · Cuenta · Documento Tipo · Nº · Fecha Emision · Fecha Vcto. · Tipo de cambio · Saldo).",
    };
  }
  const total = rows.reduce((s, r) => s + r.saldo_clp, 0);
  return { rows, total, skipped, names };
}

export function SupplierBalanceUploadDialog({
  open,
  onOpenChange,
}: SupplierBalanceUploadDialogProps) {
  const { toast } = useToast();
  const importMutation = useImportSupplierBalances();
  const { data: companies = [] } = useCompanies();
  const [companyId, setCompanyId] = useState<string>(ALIACE_SPA_ID);
  const [cutoffDate, setCutoffDate] = useState("2026-07-31");
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [warning, setWarning] = useState<ImportWarning | null>(null);

  const reset = () => {
    setFileName(null);
    setParsed(null);
    setWarning(null);
  };

  const downloadTemplate = () => {
    const cols = [
      { wch: 12 },
      { wch: 36 },
      { wch: 10 },
      { wch: 14 },
      { wch: 10 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 16 },
    ];
    const wb = XLSX.utils.book_new();
    const wsE = XLSX.utils.json_to_sheet([
      {
        Codigo: "70086991",
        Nombre: "BUNGE ARGENTINA S.A",
        Cuenta: "2103002",
        "Documento Tipo": "C2",
        "Nº": "27952",
        "Fecha Emision": "2024-12-26",
        "Fecha Vcto.": "2025-02-24",
        "Tipo de cambio": 924.78,
        Saldo: 72141246,
      },
    ]);
    wsE["!cols"] = cols;
    const wsN = XLSX.utils.json_to_sheet([
      {
        Codigo: "76425115",
        Nombre: "BUNGE CHILE SPA",
        Cuenta: "2103001",
        "Documento Tipo": "FC",
        "Nº": "12345",
        "Fecha Emision": "2026-07-15",
        "Fecha Vcto.": "2026-08-14",
        "Tipo de cambio": "",
        Saldo: 5000000,
      },
    ]);
    wsN["!cols"] = cols;
    XLSX.utils.book_append_sheet(wb, wsE, "Exterior");
    XLSX.utils.book_append_sheet(wb, wsN, "Nacional");
    XLSX.writeFile(wb, "formato_carga_saldos.xlsx");
  };

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);
    setParsed(null);
    setWarning(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        setParsed(parseWorkbook(e.target?.result as ArrayBuffer));
      } catch (err) {
        setParsed({
          rows: [],
          total: 0,
          skipped: 0,
          names: {},
          fatal: err instanceof Error ? err.message : "No se pudo leer el archivo",
        });
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleUpload = async () => {
    if (!parsed || parsed.rows.length === 0) return;
    setWarning(null);
    try {
      const result = await importMutation.mutateAsync({
        cutoffDate,
        rows: parsed.rows,
        companyId,
      });

      if (result.unmatched.length > 0) {
        // Partial load: some códigos have no matching supplier. Surface them persistently
        // (with the amount left out) instead of a toast that vanishes, so nothing is dropped
        // silently. Keep the dialog + parsed file so the user can re-upload after linking them.
        const details: UnmatchedDetail[] = result.unmatched.map((code) => ({
          code,
          nombre: parsed.names[code] ?? "",
          monto: parsed.rows
            .filter((r) => r.codigo === code)
            .reduce((s, r) => s + r.saldo_clp, 0),
        }));
        setWarning({
          imported: result.imported,
          details,
          total: details.reduce((s, d) => s + d.monto, 0),
        });
        toast({
          title: "Carga parcial",
          description: `${result.imported} partidas cargadas · ${result.unmatched.length} códigos sin proveedor`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Saldos cargados",
        description: `${result.imported} partidas cargadas`,
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: "Error al cargar",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Carga de Saldos
          </DialogTitle>
          <DialogDescription>
            Suba el reporte de Softland con las partidas abiertas por proveedor. Reemplaza todos los
            saldos iniciales de la fecha de corte seleccionada. Los proveedores se enlazan por su
            código Softland.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-wrap gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="balance-empresa">Empresa</Label>
                <Select value={companyId} onValueChange={setCompanyId}>
                  <SelectTrigger id="balance-empresa" className="h-9 w-48">
                    <SelectValue placeholder="Empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="cutoff">Fecha de corte</Label>
                <Input
                  id="cutoff"
                  type="date"
                  value={cutoffDate}
                  onChange={(e) => setCutoffDate(e.target.value)}
                  className="h-9 w-44"
                />
              </div>
            </div>
            <Button variant="outline" size="sm" className="h-9" onClick={downloadTemplate}>
              <Download className="mr-2 h-4 w-4" />
              Descargar formato base
            </Button>
          </div>

          <Input
            type="file"
            accept=".xlsx,.xls"
            disabled={importMutation.isPending}
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="h-auto cursor-pointer py-2 file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
          />

          {parsed?.fatal && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {parsed.fatal}
            </div>
          )}

          {parsed && !parsed.fatal && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              {fileName && <span className="font-medium">{fileName}</span>}{" "}
              — <span className="text-green-700">{parsed.rows.length} partidas</span>
              {parsed.skipped > 0 && (
                <span className="text-muted-foreground"> · {parsed.skipped} totales omitidos</span>
              )}
              <div className="mt-1 text-muted-foreground">
                Saldo total: <span className="font-medium">${formatCurrency(parsed.total)}</span>
              </div>
            </div>
          )}

          {warning && (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium">
                {warning.details.length} códigos sin proveedor · $
                {formatCurrency(warning.total)} no importados
              </p>
              <p className="text-amber-800">
                {warning.imported} partidas se cargaron. Estos códigos no están enlazados a ningún
                proveedor por su código Softland, así que quedaron fuera. Cree o enlace estos
                proveedores y vuelva a subir el archivo.
              </p>
              <ul className="max-h-40 space-y-1 overflow-auto">
                {warning.details.map((d) => (
                  <li key={d.code} className="flex justify-between gap-4 tabular-nums">
                    <span className="truncate">
                      {d.code}
                      {d.nombre ? ` · ${d.nombre}` : ""}
                    </span>
                    <span className="shrink-0">${formatCurrency(d.monto)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            disabled={
              !parsed || parsed.rows.length === 0 || !cutoffDate || importMutation.isPending
            }
          >
            {importMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            Cargar {parsed?.rows.length ? `(${parsed.rows.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
